import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Client, CLIENT_NAME_MAX } from '../../types';
import * as api from '../../services/api';

/**
 * Client combobox — "pick an existing client, or type a new name".
 *
 * The outbound twin of SourceSelect, and deliberately the same interaction:
 * a plain input plus a filtered list, never a <select>. A <select> can't
 * express "or type something new", and an autocomplete that silently snaps to
 * the nearest existing name is how leads end up delivered under the wrong
 * buyer — which, once one lead has gone out, is permanent (see the assignment
 * lock in migration 031).
 *
 * Reports its value as a discriminated pair because the two cases hit
 * different fields on the API:
 *
 *   { clientId }   — an existing client was picked from the list
 *   { clientName } — a name that isn't a client yet; the backend creates it
 *                    (case-insensitively de-duped) as part of the write
 *
 * Names are hard-capped at CLIENT_NAME_MAX characters. The cap is enforced
 * here (maxLength + the counter), at the API layer, and by a CHECK in the
 * migration — all three on purpose.
 */

export type ClientSelection =
  | { kind: 'existing'; clientId: string; name: string }
  | { kind: 'new'; name: string }
  | null;

interface Props {
  value: ClientSelection;
  onChange: (value: ClientSelection) => void;
  /** Rendered under the field. Use for save errors from the server. */
  error?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

export default function ClientSelect({
  value,
  onChange,
  error,
  autoFocus,
  disabled,
  placeholder,
}: Props) {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [text, setText] = useState(value?.name ?? '');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    api.getClients()
      .then(list => { if (!cancelled) { setClients(list); setLoadError(null); } })
      .catch(err => {
        if (cancelled) {return;}
        // A failed list must NOT block assigning a client — the user can still
        // type a name and the backend will find-or-create it.
        setLoadError(err?.response?.data?.error || err?.message || 'Could not load clients');
      })
      .finally(() => { if (!cancelled) {setLoading(false);} });
    return () => { cancelled = true; };
  }, []);

  // Keep the text box in sync when the PARENT changes the field. The trimmed
  // comparison matters: every keystroke calls onChange, so `value` is a new
  // object each time, and assigning `value.name` unconditionally would eat a
  // trailing space the moment the user typed one.
  useEffect(() => {
    if (!value) { setText(''); return; }
    setText(prev => (prev.trim() === value.name ? prev : value.name));
  }, [value]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {setOpen(false);}
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const matches = useMemo(() => {
    const q = text.trim().toLowerCase();
    if (!q) {return clients;}
    return clients.filter(c => c.name.toLowerCase().includes(q));
  }, [clients, text]);

  // An exact (case-insensitive) hit means typing the name is the same thing as
  // picking it, so we must NOT offer "Create …" for it or the user gets a 409
  // from the unique index.
  const exact = useMemo(
    () => clients.find(c => c.name.toLowerCase() === text.trim().toLowerCase()) || null,
    [clients, text]
  );

  function commitText(raw: string) {
    const name = raw.trim();
    if (!name) {return onChange(null);}
    const hit = clients.find(c => c.name.toLowerCase() === name.toLowerCase());
    onChange(hit ? { kind: 'existing', clientId: hit.id, name: hit.name } : { kind: 'new', name });
  }

  function pick(c: Client) {
    setText(c.name);
    onChange({ kind: 'existing', clientId: c.id, name: c.name });
    setOpen(false);
  }

  const overLimit = text.trim().length > CLIENT_NAME_MAX;
  const showCreateRow = text.trim().length > 0 && !exact && !overLimit;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        className="form-input"
        type="text"
        maxLength={CLIENT_NAME_MAX}
        placeholder={loading ? 'Loading clients…' : (placeholder || 'e.g., Acme Insurance')}
        value={text}
        disabled={disabled}
        autoFocus={autoFocus}
        onChange={e => { setText(e.target.value); setOpen(true); commitText(e.target.value); }}
        onFocus={() => setOpen(true)}
        onKeyDown={e => {
          if (e.key === 'Escape') {setOpen(false);}
          if (e.key === 'Enter' && open) {
            // Enter inside an open dropdown picks the single obvious match
            // instead of submitting the form behind it.
            if (matches.length === 1) { e.preventDefault(); pick(matches[0]); }
            else {setOpen(false);}
          }
        }}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls="client-select-listbox"
      />

      {open && !disabled && (matches.length > 0 || showCreateRow) && (
        <div
          id="client-select-listbox"
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 50,
            marginTop: 4,
            maxHeight: 240,
            overflowY: 'auto',
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            boxShadow: '0 8px 20px rgba(0,0,0,0.10)',
          }}
        >
          {matches.map(c => (
            <button
              key={c.id}
              type="button"
              role="option"
              aria-selected={value?.kind === 'existing' && value.clientId === c.id}
              onClick={() => pick(c)}
              style={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '9px 12px',
                border: 'none',
                borderBottom: '1px solid #f3f4f6',
                background: 'transparent',
                textAlign: 'left',
                fontSize: 14,
                color: '#111827',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontWeight: 500 }}>{c.name}</span>
              <span style={{ fontSize: 12, color: '#9ca3af' }}>
                {c.nodeCount
                  ? `${c.nodeCount} ${c.nodeCount === 1 ? 'destination' : 'destinations'}`
                  : 'no destinations yet'}
              </span>
            </button>
          ))}

          {showCreateRow && (
            <button
              type="button"
              onClick={() => { commitText(text); setOpen(false); }}
              style={{
                display: 'block',
                width: '100%',
                padding: '9px 12px',
                border: 'none',
                background: '#f9fafb',
                textAlign: 'left',
                fontSize: 13,
                color: '#1d4ed8',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              + Create new client “{text.trim()}”
            </button>
          )}
        </div>
      )}

      {value?.kind === 'new' && !overLimit && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#92400e' }}>
          New client — “{value.name}” will be added to your Clients directory.
        </div>
      )}
      {text.length >= CLIENT_NAME_MAX - 5 && (
        <div style={{ marginTop: 6, fontSize: 12, color: overLimit ? '#dc2626' : '#9ca3af' }}>
          {text.trim().length}/{CLIENT_NAME_MAX} characters
        </div>
      )}
      {loadError && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#b45309' }}>
          {loadError}. You can still type a client name.
        </div>
      )}
      {error && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#dc2626' }}>{error}</div>
      )}
    </div>
  );
}
